/**
 * ExposureSyncText — keyword highlighter that locks ALL listed
 * keywords to the exposure-card's score animation.
 *
 * The exposure card animates a score from 8 (LOW · green) to 84
 * (CRITICAL · red) over 7s, then snaps back over 1s, looping forever.
 * This component runs the IDENTICAL `requestAnimationFrame` clock and
 * interpolates the SAME RGB stops, so every highlighted keyword in the
 * hero title shifts color in perfect lockstep with the card's gauge.
 *
 * Implementation notes:
 *  - We replicate AnimatedExposureCard's 7000ms / 1000ms cycle and the
 *    same `COLOR_STOPS` so the colors actually match. Don't change one
 *    without the other.
 *  - All keywords pulse together — one shared color per frame. This is
 *    intentional (user requested "in lockstep with each other").
 *  - `prefers-reduced-motion` short-circuits to a static green so the
 *    text stays readable for low-vibration users.
 */
import React, { useEffect, useState, useMemo } from "react";

// Must mirror /app/frontend/src/components/AnimatedExposureCard.jsx
// Stops are evenly spaced 0/33/66/100 so each colour segment has equal
// runtime — gives a smooth continuous fade through all four tiers.
const COLOR_STOPS = [
  { at: 0,   color: "#1fdd83" },
  { at: 33,  color: "#FFB000" },
  { at: 66,  color: "#FF8A1F" },
  { at: 100, color: "#FF2A2A" },
];
const MIN_SCORE = 8;
const MAX_SCORE = 84;
const HOLD_MS   = 2500;
const RAMP_MS   = 8000;
const RESET_MS  = 2000;
const CYCLE_MS  = HOLD_MS + RAMP_MS + RESET_MS;

function lerpHex(a, b, t) {
  const ah = a.replace("#", "");
  const bh = b.replace("#", "");
  const ar = parseInt(ah.slice(0, 2), 16);
  const ag = parseInt(ah.slice(2, 4), 16);
  const ab = parseInt(ah.slice(4, 6), 16);
  const br = parseInt(bh.slice(0, 2), 16);
  const bg = parseInt(bh.slice(2, 4), 16);
  const bb = parseInt(bh.slice(4, 6), 16);
  return `rgb(${Math.round(ar + (br - ar) * t)},`
       + `${Math.round(ag + (bg - ag) * t)},`
       + `${Math.round(ab + (bb - ab) * t)})`;
}

function colorForScore(score) {
  // Remap score (8-84) to 0-100 progress so endpoints are PURE green/red.
  const span = (MAX_SCORE - MIN_SCORE) || 1;
  const p = Math.max(0, Math.min(100, ((score - MIN_SCORE) / span) * 100));
  if (p <= COLOR_STOPS[0].at) return COLOR_STOPS[0].color;
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    const a = COLOR_STOPS[i];
    const b = COLOR_STOPS[i + 1];
    if (p <= b.at) {
      const t = (p - a.at) / (b.at - a.at);
      return lerpHex(a.color, b.color, t);
    }
  }
  return COLOR_STOPS[COLOR_STOPS.length - 1].color;
}

/**
 * Drop-in: takes a multi-line text node + an array of keywords. Highlighted
 * keyword tokens cycle through exposure colors, everything else stays
 * `baseColor` (defaults to inherit/white).
 */
export default function ExposureSyncText({
  text,
  keywords = [],
  baseColor = "inherit",
  className = "",
  style = {},
  testId,
}) {
  const [score, setScore] = useState(MIN_SCORE);

  useEffect(() => {
    // Respect reduced-motion: pin to LOW (green) and skip rAF.
    const prefersReduced = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;
    if (prefersReduced) {
      setScore(MIN_SCORE);
      return;
    }
    let raf;
    // Cubic ease-in-out — must match AnimatedExposureCard so the title
    // and gauge climb at the EXACT same rate.
    const easeInOut = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const tick = (ts) => {
      // Use the global clock (no per-mount startedAt) so this component
      // is in lockstep with AnimatedExposureCard regardless of mount order.
      const elapsed = ts % CYCLE_MS;
      let s;
      if (elapsed < HOLD_MS) {
        s = MIN_SCORE;
      } else if (elapsed < HOLD_MS + RAMP_MS) {
        const t = easeInOut((elapsed - HOLD_MS) / RAMP_MS);
        s = MIN_SCORE + (MAX_SCORE - MIN_SCORE) * t;
      } else {
        const t = (elapsed - HOLD_MS - RAMP_MS) / RESET_MS;
        s = MAX_SCORE - (MAX_SCORE - MIN_SCORE) * t;
      }
      setScore(s);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const swatch = colorForScore(score);

  // Split text on the keyword set. Sort by length desc so phrases like
  // "Risk Intelligence" match before "Risk".
  const segments = useMemo(() => {
    if (!keywords.length) return [{ text }];
    const sorted = [...keywords].sort((a, b) => b.length - a.length);
    const escaped = sorted.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const re = new RegExp(`(${escaped.join("|")})`, "g");
    const parts = text.split(re);
    const set = new Set(sorted);
    return parts.map((p) => ({ text: p, kw: set.has(p) }));
  }, [text, keywords]);

  return (
    <span className={className} style={style} data-testid={testId}>
      {segments.map((seg, i) =>
        seg.kw ? (
          <span key={i}
                style={{
                  color: swatch,
                  transition: "color 60ms linear",
                }}>
            {seg.text}
          </span>
        ) : (
          <span key={i} style={{ color: baseColor }}>{seg.text}</span>
        ),
      )}
    </span>
  );
}
